import {
  buildPlayDetailPath,
  buildPostDetailPath,
  getPostPublicId,
  getPublicId,
  isVideoMediaType,
} from "./url-policy.mjs";

const isObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const getFirstArrayItem = (value) => (Array.isArray(value) ? value[0] : null);

export const getContentId = (content) =>
  typeof content === "string"
    ? getPostPublicId(content)
    : getPublicId(content) || getPostPublicId(content);

export const getContentPublicId = (content) => getPostPublicId(content);

export const getContentMedia = (content) =>
  Array.isArray(content?.media) ? content.media.filter(Boolean) : [];

export const getContentCoverMedia = (content) =>
  Array.isArray(content?.postMediaCover)
    ? content.postMediaCover.filter(Boolean)
    : [];

export const getPrimaryMedia = (content) => getFirstArrayItem(getContentMedia(content));

export const getPrimaryCoverMedia = (content) =>
  getFirstArrayItem(getContentCoverMedia(content));

export const getMediaType = (media) =>
  String(media?.mediaType || media?.type || "").toLowerCase();

export const getPrimaryMediaType = (content) => getMediaType(getPrimaryMedia(content));

export const isVideoMedia = (media) => isVideoMediaType(getMediaType(media));

export const isImageMedia = (media) => getMediaType(media).includes("image");
export const isVideoContent = (content) => isVideoMedia(getPrimaryMedia(content));

const GENERIC_VIDEO_POSTER_MARKERS = [
  "/images/main-logo",
  "main-logo.png",
  "/camera-icon",
  "camera-icon.png",
  "/no-data",
  "no-data.png",
];

export const isGenericVideoPosterPath = (path) => {
  if (!path || typeof path !== "string") return false;
  const normalizedPath = path.toLowerCase();

  return GENERIC_VIDEO_POSTER_MARKERS.some((marker) =>
    normalizedPath.includes(marker)
  );
};

export const getVideoPreviewPath = (path) => {
  if (!path || typeof path !== "string") return "";
  if (path.startsWith("data:") || path.includes("#")) return path;

  return `${path}#t=0.1`;
};

export const isPublicContent = (content) => content?.isPublic !== false;

export const isPlayableShortContent = (content) =>
  content?.isPublic === true && getPrimaryMediaType(content) === "video/mp4";

export const getContentKind = (content) =>
  isVideoContent(content) ? "play" : "post";

export const getContentDetailPath = (content) =>
  isVideoContent(content)
    ? buildPlayDetailPath(content)
    : buildPostDetailPath(content);

export const getContentFilePath = (content) => getPrimaryMedia(content)?.filePath || "";

export const getContentPosterPath = (content) => {
  const media = getPrimaryMedia(content);
  const cover = getPrimaryCoverMedia(content);
  const isVideo = isVideoContent(content);

  const posterPath =
    cover?.thumbnailPath ||
    cover?.mediumPath ||
    cover?.coverPath ||
    media?.thumbnailPath ||
    media?.placeholderPath ||
    media?.placeholder ||
    media?.mediumPath ||
    media?.largePath ||
    "";

  if (isVideo && isGenericVideoPosterPath(posterPath)) return "";

  return posterPath;
};

export const getImageMediaItems = (content) =>
  getContentMedia(content).filter(isImageMedia);

export const normalizeContentItem = (content) => {
  if (!isObject(content)) {
    const publicId = getContentPublicId(content);

    return {
      raw: content,
      id: publicId,
      publicId,
      kind: "post",
      media: [],
      primaryMedia: null,
      mediaType: "",
      isVideo: false,
      isPublic: true,
      place: null,
      title: "",
      description: "",
      user: null,
      detailPath: publicId ? buildPostDetailPath(publicId) : "/play",
    };
  }

  const primaryMedia = getPrimaryMedia(content);
  const isVideo = isVideoContent(content);

  return {
    raw: content,
    id: getContentId(content),
    publicId: getContentPublicId(content),
    kind: isVideo ? "play" : "post",
    media: getContentMedia(content),
    primaryMedia,
    mediaType: getMediaType(primaryMedia),
    isVideo,
    isPublic: isPublicContent(content),
    place: content.place || null,
    title: content.title || "",
    description: content.description || "",
    user: content.user || null,
    detailPath: getContentDetailPath(content),
  };
};
